import * as React from 'react';
import { ColumnDef, Row, Table } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { navigate } from 'wouter/use-location';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import IconCell from '../CmdTable/TableCells/IconCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThOnlySort from '../CmdTable/TableHeadCell/ThOnlySort';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import ThText from '../CmdTable/TableHeadCell/ThText';
import LinkCell from '../CmdTable/TableCells/LinkCell';
import { ACTIONS_KEY, CHECKBOX_KEY, SECONDARY_ACTIONS_KEY } from '../CmdTable/constants';
import IndeterminateCheckbox from '../CmdTable/IndeterminateCheckbox';
import {
  TABLE_REFUELING_KEY,
  TABLE_REFUELING_LOCKED,
  TABLE_REFUELING_ACCOUNTING_TS,
  TABLE_REFUELING_CUSTOMER,
  TABLE_REFUELING_VEHICLE_LICENCE_NUMBER,
  TABLE_REFUELING_VEHICLE_NAME,
  TABLE_REFUELING_ASSIGNED_TO_RIDE,
  TABLE_REFUELING_GAS_STATION_NETWORK_ID,
  TABLE_REFUELING_FUEL_TYPE,
  TABLE_REFUELING_AMOUNT,
  TABLE_REFUELING_VAT,
  TABLE_REFUELING_UNIT_PRICE_INC_VAT,
  TABLE_REFUELING_UNIT_PRICE,
  TABLE_REFUELING_PRICE,
  TABLE_REFUELING_VAT_PRICE,
  TABLE_REFUELING_PRICE_INC_VAT,
  TABLE_REFUELING_CURRENCY,
  TABLE_REFUELING_ANOTHER_CURRENCY,
  TABLE_REFUELING_NOTE,
  TABLE_REFUELING_ATTACHMENTS,
  TABLE_REFUELING_SOURCE,
  TABLE_REFUELING_MILEAGE,
  TABLE_REFUELING_AVERAGE_CONSUMPTION,
  TABLE_REFUELING_CREATED_BY,
  TABLE_REFUELING_CREATED_AT,
  TABLE_REFUELING_BILL,
  TABLE_REFUELING_MODIFIED_BY,
  TABLE_REFUELING_MODIFIED_AT,
  TABLE_REFUELING_MILE_AGE_DIFF_KM,
} from './constants';
import { ICmdTableCustomerCell } from '../Codebooks/types';
import ThRange from '../CmdTable/TableHeadCell/ThRange';
import { RefuelingsRecord } from './types';
import ThLockded from '../CmdTable/TableHeadCell/ThLocked';
import SvgIconService from '../../Services/SvgIconService';
import { LOCKED_KEY, UNLOCKED_KEY } from '../TripsOverviewNew/constants';
import { TABLE_REFUELING } from '../../Services/TableService';
import { URL_REFUELING } from '../../router/constants';
import { putRequest } from '../../Services/HttpService';
import DetailActionRefueling from './RowActions/DetailActionsRefueling';
import DeleteAction from '../CmdTable/TableActions/DeleteAction';
import DownloadCell from '../CmdTable/TableCells/DownloadCell';
import { columnsAtom } from '../CmdTable/CmdTableState';

interface IUseRefueling {
  columns: ColumnDef<RefuelingsRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useRefuelings(): IUseRefueling {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);

  const handleIndeterminateChange = (table: Table<RefuelingsRecord>) => {
    if (table.getIsSomeRowsSelected()) {
      table.toggleAllRowsSelected(false);
    } else {
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
    }
  };

  const handleCheckboxChange = (
    event: React.FormEvent<HTMLInputElement>,
    row: Row<RefuelingsRecord>
  ) => {
    row.getToggleSelectedHandler()(event);
  };

  const updateTable = React.useCallback(() => {
    queryClient.resetQueries({ queryKey: [TABLE_REFUELING_KEY] });
  }, [queryClient]);

  const handleRemove = React.useCallback(
    async (id: number, customerId?: number | number[]) => {
      if (customerId && !Array.isArray(customerId)) {
        const ids = { [customerId]: [id] };
        const response = await putRequest(`/v1/${TABLE_REFUELING}/remove`, { ids });
        if (response) {
          updateTable();
        }
      }
    },
    [updateTable]
  );

  const handleAction = (data: RefuelingsRecord) => {
    navigate(
      `${URL_REFUELING}/edit/${data.id}?customerId=${data.customer.id}&locked=${data.locked}`
    );
  };

  const handleLockedIcon = React.useCallback(
    (name: string): JSX.Element => {
      const restrictedIcon = SvgIconService.adminLock();

      switch (name) {
        case LOCKED_KEY:
          return (
            <i
              className="cmd-icon cmd-icon-lock text-primary fs-4"
              title={f({ id: 'tables.tripsOverview.icon.tooltip.locked' })}
            />
          );
        case UNLOCKED_KEY:
          return (
            <i
              className="cmd-icon cmd-icon-unlock text-success fs-4"
              title={f({ id: 'tables.tripsOverview.icon.tooltip.unlocked' })}
            />
          );
        default:
          return (
            <img
              src={restrictedIcon}
              alt="admin-lock"
              title={f({ id: 'tables.tripsOverview.icon.tooltip.restricted' })}
            />
          );
      }
    },
    [f]
  );

  const handleSourceText = React.useCallback(
    (source: string): string => {
      switch (source) {
        case 'import':
          return f({ id: `enum.refuelings.type.import` });
        case 'autoimport':
          return f({ id: `enum.refuelings.type.autoimport` });
        case 'manual':
          return f({ id: `enum.refuelings.type.manual` });
        case 'source':
          return f({ id: `enum.refuelings.type.source` });
        default:
          return source.charAt(0).toUpperCase() + source.slice(1);
      }
    },
    [f]
  );

  const columns = React.useMemo<ColumnDef<RefuelingsRecord>[]>(
    () => [
      {
        id: CHECKBOX_KEY,
        header: ({ table }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkallrecords"
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={() => handleIndeterminateChange(table)}
              className="custom-checkbox"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkOneRecord"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={(event: React.FormEvent<HTMLInputElement>) =>
                handleCheckboxChange(event, row)
              }
              className="custom-checkbox"
            />
          </div>
        ),

        size: 40,
        enablePinning: true,
      },
      {
        accessorKey: TABLE_REFUELING_LOCKED,
        header: (_table) => (
          <ThLockded
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_LOCKED}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_LOCKED}` })}
            tooltip={f({ id: `table_list.columns.${TABLE_REFUELING_LOCKED}` })}
          />
        ),
        cell: (info) =>
          info.row.original?.locked ? (
            <IconCell element={handleLockedIcon(info.getValue<string>())} />
          ) : null,
        size: 40,
      },
      {
        accessorKey: TABLE_REFUELING_ACCOUNTING_TS,
        header: (_table) => (
          <ThDateTime
            id={TABLE_REFUELING_ACCOUNTING_TS}
            tableName={TABLE_REFUELING_KEY}
            title={f({ id: `table_list.columns.refuelings.timestamp` })}
            isSortable={true}
            alwaysActive
          />
        ),
        cell: (info) =>
          info.row.original?.accountingTs ? (
            <DateTimeCell datetime={info.getValue<string>() || ''} format="DD.MM.YYYY HH:mm" />
          ) : null,
        minSize: 260,
        footer: (_props) => <TextCell text={f({ id: 'table_list.summary.grandRowsTotal' })} />,
      },

      {
        accessorKey: TABLE_REFUELING_AMOUNT,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_AMOUNT}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_AMOUNT}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${TABLE_REFUELING_AMOUNT}.tooltip` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.amount ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        minSize: 200,
      },
      {
        accessorKey: TABLE_REFUELING_VAT,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_VAT}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_VAT}` })}
            isSortable={true}
            maxValue="100"
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.vat ? <TextCell text={info.getValue<string>()} align="right" /> : null,
        size: 140,
      },
      {
        accessorKey: TABLE_REFUELING_UNIT_PRICE_INC_VAT,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_UNIT_PRICE_INC_VAT}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_UNIT_PRICE_INC_VAT}` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.unitPriceIncVat ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        size: 200,
      },
      {
        accessorKey: TABLE_REFUELING_UNIT_PRICE,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_UNIT_PRICE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_UNIT_PRICE}` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.unitPrice ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        size: 200,
      },
      {
        accessorKey: TABLE_REFUELING_PRICE,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_PRICE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_PRICE}` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.price ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        size: 160,
      },
      {
        accessorKey: TABLE_REFUELING_VAT_PRICE,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_VAT_PRICE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_VAT_PRICE}` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.vatPrice ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        size: 160,
      },
      {
        accessorKey: TABLE_REFUELING_PRICE_INC_VAT,
        header: (_table) => (
          <ThRange
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_PRICE_INC_VAT}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_PRICE_INC_VAT}` })}
            isSortable={true}
            unit=""
          />
        ),
        cell: (info) =>
          info.row.original?.priceIncVat ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        size: 200,
      },
      {
        accessorKey: TABLE_REFUELING_CURRENCY,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_CURRENCY}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_CURRENCY}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.currency ? <TextCell text={info.getValue<string>()} /> : null,
        minSize: 100,
      },
      {
        accessorKey: TABLE_REFUELING_ANOTHER_CURRENCY,
        header: (_table) => (
          <ThSimpleSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_ANOTHER_CURRENCY}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_ANOTHER_CURRENCY}` })}
          />
        ),
        cell: (info) =>
          info.row.original?.anotherCurrency ? (
            <TextCell text={f({ id: 'enum.boolean.true' })} />
          ) : null,
        size: 160,
      },
      {
        accessorKey: TABLE_REFUELING_NOTE,
        header: (_table) => (
          <ThSearch
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_NOTE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_NOTE}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.note ? <TextCell text={info.getValue<string>()} /> : null,
        size: 360,
      },
      {
        accessorKey: TABLE_REFUELING_ATTACHMENTS,
        header: (_table) => (
          <ThText
            // tableName={TABLE_REFUELING_KEY}
            // id={TABLE_REFUELING_ATTACHMENTS}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_ATTACHMENTS}` })}
            // tooltip={f({ id: `table_list.columns.${TAG_CUSTOMER_ID_KEY}.tooltip` })}
            // isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.attachments ? (
            <DownloadCell data={info.row.original} align="left" />
          ) : null,
        minSize: 200,
      },
      {
        accessorKey: TABLE_REFUELING_SOURCE,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_SOURCE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_SOURCE}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.source ? (
            <TextCell text={handleSourceText(info.getValue<string>())} />
          ) : null,
        minSize: 140,
      },
      {
        accessorKey: TABLE_REFUELING_MILEAGE,
        header: (_table) => (
          <ThOnlySort
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_MILEAGE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_MILEAGE}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.mileage ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        minSize: 160,
      },
      {
        accessorKey: TABLE_REFUELING_MILE_AGE_DIFF_KM,
        header: (_table) => (
          <ThOnlySort
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_MILE_AGE_DIFF_KM}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_MILE_AGE_DIFF_KM}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.mileageDiffKm ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        minSize: 140,
      },
      {
        accessorKey: TABLE_REFUELING_AVERAGE_CONSUMPTION,
        header: (_table) => (
          <ThOnlySort
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_AVERAGE_CONSUMPTION}
            title={`${f({
              id: `reports.R07.consumption`,
            })}`}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.averageConsumption ? (
            <TextCell text={info.getValue<string>()} align="right" />
          ) : null,
        minSize: 200,
      },
      {
        accessorKey: TABLE_REFUELING_CREATED_BY,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_CREATED_BY}
            title={f({
              id: `table_list.columns.createdBy`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<{ id: number; name: string }>().name || ''} />
          ) : null,
        minSize: 200,
      },
      {
        accessorKey: TABLE_REFUELING_CREATED_AT,
        header: (_table) => (
          <ThDateTime
            id={TABLE_REFUELING_CREATED_AT}
            tableName={TABLE_REFUELING_KEY}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_CREATED_AT}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="DD.MM.YYYY HH:mm" />
          ) : null,
        size: 150,
      },
      {
        accessorKey: TABLE_REFUELING_MODIFIED_BY,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_MODIFIED_BY}
            title={f({
              id: `table_list.columns.${TABLE_REFUELING_MODIFIED_BY}`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<{ id: number; name: string }>().name || ''} />
          ) : null,
        minSize: 180,
      },
      {
        accessorKey: TABLE_REFUELING_MODIFIED_AT,
        header: (_table) => (
          <ThDateTime
            id={TABLE_REFUELING_MODIFIED_AT}
            tableName={TABLE_REFUELING_KEY}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_MODIFIED_AT}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedAt ? (
            <DateTimeCell datetime={info.getValue<string | null>()} format="DD.MM.YYYY HH:mm" />
          ) : null,
        minSize: 150,
      },
      {
        accessorKey: TABLE_REFUELING_GAS_STATION_NETWORK_ID,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_GAS_STATION_NETWORK_ID}
            title={f({
              id: `table_list.columns.gasStationNetworkId`,
            })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.gasStationNetwork ? <TextCell text={info.getValue<string>()} /> : null,
        size: 180,
      },
      {
        accessorKey: TABLE_REFUELING_FUEL_TYPE,
        header: (_table) => (
          <ThMultiSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_FUEL_TYPE}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_FUEL_TYPE}Id` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.fuelType ? <TextCell text={info.getValue<string>()} /> : null,
        size: 140,
      },
      {
        accessorKey: TABLE_REFUELING_VEHICLE_NAME,
        header: (_table) => (
          <ThMultiSelect
            id={TABLE_REFUELING_VEHICLE_NAME}
            tableName={TABLE_REFUELING_KEY}
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_VEHICLE_NAME}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          // eslint-disable-next-line prettier/prettier
          info.row.original?.vehicleName ? (
            <TextCell text={info.getValue<string>() || ''} />
          ) : null,
        size: 200,
      },
      {
        accessorKey: TABLE_REFUELING_VEHICLE_LICENCE_NUMBER,
        header: (_table) => (
          <ThMultiSelect
            id={TABLE_REFUELING_VEHICLE_LICENCE_NUMBER}
            tableName={TABLE_REFUELING_KEY}
            title={f({
              id: `table_list.columns.refuelings.${TABLE_REFUELING_VEHICLE_LICENCE_NUMBER}`,
            })}
          />
        ),
        cell: (info) =>
          info.row.original?.licenceNumber ? (
            <TextCell text={info.row.original?.licenceNumber || ''} />
          ) : null,
        size: 100,
      },

      {
        accessorKey: TABLE_REFUELING_ASSIGNED_TO_RIDE,
        header: (_table) => (
          <ThText
            title={f({ id: `table_list.columns.refuelings.${TABLE_REFUELING_ASSIGNED_TO_RIDE}` })}
          />
        ),
        cell: (info) =>
          info.row.original?.assignedToRide && info.row.original?.assignedToRide?.value ? (
            <LinkCell
              text={info.row.original?.assignedToRide?.value}
              link={info.row.original?.assignedToRide?.link}
            />
          ) : null,
        size: 300,
      },
      {
        accessorKey: TABLE_REFUELING_BILL,
        header: (_table) => (
          <ThSearch
            id={TABLE_REFUELING_BILL}
            tableName={TABLE_REFUELING_KEY}
            title={f({ id: `table_list.columns.${TABLE_REFUELING_BILL}Id` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.vehicleName ? (
            <TextCell text={info.getValue<string>() || ''} align="right" />
          ) : null,
        minSize: 200,
      },
      {
        accessorKey: TABLE_REFUELING_CUSTOMER,
        header: (_table) => (
          <ThSimpleSelect
            tableName={TABLE_REFUELING_KEY}
            id={TABLE_REFUELING_CUSTOMER}
            title={f({ id: `table_list.columns.customerId` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <CustomerCell customer={info.getValue<ICmdTableCustomerCell>()} />
          ) : null,
        min: 260,
      },

      {
        accessorKey: ACTIONS_KEY,
        header: () => (
          <div className=" th-sticky sticky-col detail">{f({ id: 'table.detailBtn' })}</div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DetailActionRefueling
              id={Number(info.row.original.id)}
              tableName={TABLE_REFUELING}
              customerId={info.row.original.customer.id}
              onClickCallback={handleAction}
              data={info.row.original}
            />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
      {
        accessorKey: SECONDARY_ACTIONS_KEY,
        header: () => (
          <div className=" th-sticky sticky-col detail">{f({ id: 'table.deleteBtn' })}</div>
        ),
        cell: (info) =>
          info.row.original ? (
            <DeleteAction
              id={Number(info.row.original.id)}
              tableName={TABLE_REFUELING}
              customerId={info.row.original.customer.id}
              onClickCallback={handleRemove}
              showConfirmDialog
            />
          ) : null,
        size: 85, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, handleLockedIcon, handleRemove, handleSourceText]
  );

  const columnOrder = React.useMemo(() => {
    const order = [CHECKBOX_KEY, ...columnsTable.map((item) => item.key)];
    order.push(ACTIONS_KEY, SECONDARY_ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc: { [key: string]: boolean }, column: ICmdTableColumn) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
